import { useEffect, useState } from "react";
import "./index.css";

export function App() {
  const [currentJoke, setCurrentJoke] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [jokeId, setJokeId] = useState<string | null>(null);

  const fetchJoke = async (id?: string) => {
    setIsLoading(true);
    try {
      const url = id ? `/joke?id=${id}` : "/joke";
      const response = await fetch(url);
      const data = await response.json();

      setCurrentJoke(data.joke);
      setJokeId(data.id);

      // Update URL for sharing if new joke fetched
      if (data.id && !id) {
        const params = new URLSearchParams(window.location.search);
        params.set("id", data.id);
        window.history.replaceState({}, "", "?" + params.toString());
      }
    } catch (error) {
      setCurrentJoke(
        "Why don't scientists trust atoms? Because they make up everything!",
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get("id");
    fetchJoke(id ?? undefined);
  }, []);

  const getNextJoke = () => {
    fetchJoke();
  };

  const shareJoke = async () => {
    const url = window.location.href;
    copyToClipboard(url);
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      alert("Joke link copied to clipboard!");
    } catch {
      alert("Unable to copy link");
    }
  };

  return (
    <div className="min-h-screen bg-slate-800 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full text-center space-y-8">
        {/* Header */}
        <div className="space-y-2">
          <h1 className="text-4xl md:text-5xl font-bold text-white">
            Dad Jokes
          </h1>
          <p className="text-lg text-gray-300">
            The best jokes from icanhazdadjoke.com
          </p>
        </div>

        {/* Joke Display Card */}
        <div className="bg-slate-700 border border-slate-600 rounded-xl p-8 shadow-lg min-h-[120px] flex items-center justify-center">
          {isLoading ? (
            <div className="flex items-center space-x-3">
              <svg
                className="animate-spin h-6 w-6 text-yellow-400"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <span className="text-white text-lg">Loading joke...</span>
            </div>
          ) : (
            <p className="text-xl md:text-2xl text-white leading-relaxed font-medium">
              {currentJoke}
            </p>
          )}
        </div>

        {/* Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={getNextJoke}
            disabled={isLoading}
            className="bg-yellow-400 hover:bg-yellow-500 disabled:bg-yellow-300 text-slate-800 px-8 py-4 rounded-xl font-bold text-lg transition-colors duration-200 focus:outline-none focus:ring-4 focus:ring-yellow-300 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            <span>Get Next Joke</span>
          </button>

          <button
            onClick={shareJoke}
            disabled={isLoading || !currentJoke}
            className="border-2 border-yellow-400 hover:border-yellow-500 disabled:border-yellow-300 text-yellow-400 hover:text-yellow-500 disabled:text-yellow-300 px-8 py-4 rounded-xl font-bold text-lg transition-colors duration-200 focus:outline-none focus:ring-4 focus:ring-yellow-300 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"
              />
            </svg>
            <span>Share This Joke</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default App;
